import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, Modal, MoneyInput, QtyInput, useToast } from "@cx/ui";
import type { GoalListResult, GoalView, SellerView } from "@cx/contracts";
import { GOAL_METRICS, monthBounds, todayInSaoPaulo, type GoalMetric } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { GOAL_METRIC_LABEL, GOAL_STATE_LABEL } from "./labels";
import { ReportStatus, reportQueryOptions } from "./shared";
import { GoalProgress } from "./widgets";

const PATH = "/meu-negocio/metas";
const STATES = [
  ["all", "Todas"],
  ["current", "Em andamento"],
  ["future", "Futuras"],
  ["past", "Encerradas"],
] as const;

/** Metas (RF-BI-05): da empresa ou por vendedor, com o progresso pelas situações que contam como venda. */
export function GoalsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const search = useSearch({ from: PATH });
  const navigate = useNavigate({ from: PATH });
  const state = search.state ?? "all";
  const [editing, setEditing] = useState<GoalView | "new" | null>(null);
  const [deleting, setDeleting] = useState<GoalView | null>(null);
  const list = useInfiniteQuery({
    ...reportQueryOptions,
    queryKey: ["goals", companyId, state],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<GoalListResult>(
        `/goals?${new URLSearchParams({ state, ...(pageParam ? { cursor: pageParam } : {}) })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["goals", companyId] });
    void qc.invalidateQueries({ queryKey: ["dashboard", companyId] });
  };
  const del = useMutation({
    mutationFn: (id: string) =>
      api(`/goals/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { "Idempotency-Key": crypto.randomUUID() },
      }),
    onSuccess: () => {
      toast("success", "Meta excluída");
      refresh();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });

  return (
    <>
      <ContentHeader
        title="Metas"
        breadcrumb={["Meu Negócio", "Metas"]}
        actions={
          can(perms, "goals", "create") && (
            <button type="button" className="btn btn-primary" onClick={() => setEditing("new")}>
              Incluir meta
            </button>
          )
        }
      />
      <div className="box card">
        <ul className="nav nav-tabs px-3 pt-2">
          {STATES.map(([k, label]) => (
            <li className="nav-item" key={k}>
              <button
                type="button"
                className={`nav-link${state === k ? " active" : ""}`}
                aria-current={state === k ? "page" : undefined}
                onClick={() => navigate({ search: { state: k === "all" ? undefined : k }, replace: true })}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
        <ReportStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <p className="text-muted p-3 mb-0">Nenhuma meta cadastrada.</p>
        )}
        {rows.length > 0 && (
          <ul className="list-group list-group-flush" aria-label="Metas">
            {rows.map((g) => (
              <li key={g.id} className="list-group-item">
                <div className="row align-items-center">
                  <div className="col-md-9">
                    <GoalProgress g={g} />
                  </div>
                  <div className="col-md-3 text-md-end">
                    <span className="small text-muted me-2">{GOAL_STATE_LABEL[g.state]}</span>
                    {can(perms, "goals", "update") && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-1"
                        aria-label={`Editar meta ${g.name}`}
                        onClick={() => setEditing(g)}
                      >
                        Editar
                      </button>
                    )}
                    {can(perms, "goals", "delete") && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        aria-label={`Excluir meta ${g.name}`}
                        onClick={() => setDeleting(g)}
                      >
                        Excluir
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        {list.hasNextPage && (
          <div className="p-3 text-center">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={list.isFetchingNextPage}
              onClick={() => list.fetchNextPage()}
            >
              Carregar mais
            </button>
          </div>
        )}
      </div>
      {editing && (
        <GoalDialog
          goal={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Excluir meta"
        message={`Excluir a meta "${deleting?.name ?? ""}"? Ela pode ser restaurada pela Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          if (deleting) del.mutate(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

type FormValue = {
  name: string;
  metric: GoalMetric;
  scope: "company" | "seller";
  sellerId: string;
  target: number | null;
  periodStart: string;
  periodEnd: string;
};

function GoalDialog({ goal, onClose, onSaved }: { goal: GoalView | null; onClose(): void; onSaved(): void }) {
  const { companyId, me, api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [key] = useState(() => crypto.randomUUID());
  const month = monthBounds(todayInSaoPaulo().slice(0, 7));
  const [v, setV] = useState<FormValue>(() =>
    goal
      ? {
          name: goal.name,
          metric: goal.metric,
          scope: goal.scope,
          sellerId: goal.seller?.id ?? "",
          target: goal.target,
          periodStart: goal.periodStart,
          periodEnd: goal.periodEnd,
        }
      : { name: "", metric: "revenue", scope: "company", sellerId: "", target: null, ...periodOf(month) },
  );
  const canSellers = can(me.permissions as Permissions, "sellers", "read");
  const sellers = useQuery({
    queryKey: ["sellers", companyId, "active"],
    queryFn: () => api<{ data: SellerView[] }>("/sellers?status=active"),
    enabled: canSellers,
  });
  const save = useMutation({
    mutationFn: () =>
      api(goal ? `/goals/${encodeURIComponent(goal.id)}` : "/goals", {
        method: goal ? "PUT" : "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({
          ...v,
          sellerId: v.scope === "seller" ? v.sellerId || null : null,
          target: v.target ?? 0,
        }),
      }),
    onSuccess: () => {
      toast("success", goal ? "Meta alterada" : "Meta incluída");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  const money = v.metric === "revenue" || v.metric === "avg_ticket";
  return (
    <Modal
      open={open}
      title={goal ? "Editar meta" : "Nova meta"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Nome" required className="col-12" error={errors.name}>
          <input
            data-autofocus=""
            value={v.name}
            maxLength={60}
            onChange={(e) => setV({ ...v, name: e.target.value })}
          />
        </Field>
        <Field label="Métrica" required className="col-md-6" error={errors.metric}>
          <select
            className="form-select"
            value={v.metric}
            onChange={(e) => setV({ ...v, metric: e.target.value as GoalMetric, target: null })}
          >
            {GOAL_METRICS.map((m) => (
              <option key={m} value={m}>
                {GOAL_METRIC_LABEL[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Alvo" required className="col-md-6" error={errors.target}>
          {money ? (
            <MoneyInput value={v.target} onChange={(c) => setV({ ...v, target: c })} />
          ) : v.metric === "items" ? (
            <QtyInput value={v.target} onChange={(q) => setV({ ...v, target: q })} />
          ) : (
            <input
              type="number"
              min={1}
              step={1}
              className="form-control text-end"
              value={v.target ?? ""}
              onChange={(e) => setV({ ...v, target: e.target.value ? Number(e.target.value) : null })}
            />
          )}
        </Field>
        <Field label="Escopo" className="col-md-6" error={errors.scope}>
          <select
            className="form-select"
            value={v.scope}
            onChange={(e) => setV({ ...v, scope: e.target.value as FormValue["scope"] })}
          >
            <option value="company">Empresa</option>
            {canSellers && <option value="seller">Vendedor</option>}
          </select>
        </Field>
        {v.scope === "seller" && (
          <Field label="Vendedor" required className="col-md-6" error={errors.sellerId}>
            <select
              className="form-select"
              value={v.sellerId}
              onChange={(e) => setV({ ...v, sellerId: e.target.value })}
            >
              <option value="">Selecione</option>
              {sellers.data?.data.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Início" required className="col-md-6" error={errors.periodStart}>
          <input
            type="date"
            value={v.periodStart}
            onChange={(e) => setV({ ...v, periodStart: e.target.value })}
          />
        </Field>
        <Field label="Fim" required className="col-md-6" error={errors.periodEnd}>
          <input
            type="date"
            value={v.periodEnd}
            onChange={(e) => setV({ ...v, periodEnd: e.target.value })}
          />
        </Field>
      </div>
    </Modal>
  );
}

const periodOf = (m: { from: string; to: string }) => ({ periodStart: m.from, periodEnd: m.to });
